/** CrmNoteImage (Item E, slice E2) — image paste/insert → the governed asset proxy.
 *
 * An image pasted/inserted into a note is uploaded to Blob via POST /notes/{id}/assets (magic-byte +
 * size validated server-side) and the note stores only the PROXY url. Because the proxy re-gates every
 * fetch with the Entra bearer, a plain `<img src=proxyUrl>` would 401 (a browser img request can't send
 * the bearer) — so the image NODE VIEW fetches the bytes via `apiFetchBlob` (which attaches the token)
 * and displays an object URL. No public/SAS URL ever exists. */
import type { NodeViewProps } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useEffect, useState } from 'react';

import { apiFetch, apiFetchBlob } from '../../api/client';
import styles from './crm.module.css';

/** Upload one image file to a note → the persistent proxy url (stored as the image node's src). */
export async function uploadNoteImage(noteId: number, file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await apiFetch<{ asset_key: string; url: string }>(
    `/api/crm/notes/${noteId}/assets`, { method: 'POST', body: fd });
  return res.url;
}

function NoteImageView({ node }: NodeViewProps) {
  const src = node.attrs.src as string | null;
  const [objUrl, setObjUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    setObjUrl(null);
    let created: string | null = null;
    let cancelled = false;
    // ONLY the governed asset proxy is a legitimate src (a legit insert always yields a proxy url). Any
    // other src can only have entered body_json out-of-band (an API-injected external/off-proxy URL) —
    // rendering it as a raw <img> would beacon every viewer (IP/view-timing leak) + load uncontrolled
    // external content into the app origin. Refuse it (defense-in-depth; the server also strips it).
    if (src && src.startsWith('/api/crm/notes/') && src.includes('/assets/')) {
      apiFetchBlob(src)
        .then((b) => { if (!cancelled) { created = URL.createObjectURL(b); setObjUrl(created); } })
        .catch(() => { if (!cancelled) setFailed(true); });
    } else {
      setFailed(true);
    }
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [src]);
  return (
    <NodeViewWrapper as="div" className={styles.noteImageWrap}>
      {failed ? <span className={styles.noteImageError}>image unavailable</span>
        : objUrl ? <img src={objUrl} alt="" className={styles.noteImage} />
        : <span className={styles.noteImageLoading}>loading image…</span>}
    </NodeViewWrapper>
  );
}

export const NoteImage = Image.extend({
  addNodeView() { return ReactNodeViewRenderer(NoteImageView); },
}).configure({ inline: false, allowBase64: false });
